/** Safe post-auth return paths (relative, same-app only). */
const FAMILY_RETURN_STORAGE_KEY = 'valvur_family_return';
const FAMILY_INVITE_TOKEN_STORAGE_KEY = 'valvur_family_invite_token';

const SAFE_RETURN_PREFIXES = [
  '/co-parent-invite/',
  '/parent-link-approval/',
] as const;

const CO_PARENT_INVITE_PATH =
  /^\/co-parent-invite\/([^/?#]+)\/?(?:[?#].*)?$/;

export function isSafePostAuthReturnUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return false;
  }
  const path = trimmed.split('?')[0]?.split('#')[0] ?? '';
  return SAFE_RETURN_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Extract opaque invite token from a relative `/co-parent-invite/:token` URL. */
export function extractCoParentInviteToken(
  url: string | null | undefined,
): string | null {
  if (!url) {
    return null;
  }
  const path = url.trim().split('?')[0]?.split('#')[0] ?? '';
  const match = CO_PARENT_INVITE_PATH.exec(path);
  if (!match?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function buildCoParentInviteReturnUrl(token: string): string {
  return `/co-parent-invite/${encodeURIComponent(token)}`;
}

export function buildCoParentInviteIdReturnUrl(inviteId: string): string {
  return `/co-parent-invite?inviteId=${encodeURIComponent(inviteId)}`;
}

export function persistCoParentInviteToken(token: string): void {
  const trimmed = token.trim();
  if (!trimmed) {
    return;
  }
  writeStorage(FAMILY_INVITE_TOKEN_STORAGE_KEY, trimmed);
}

export function peekCoParentInviteToken(): string | null {
  return readStorage(FAMILY_INVITE_TOKEN_STORAGE_KEY);
}

export function consumeCoParentInviteToken(): string | null {
  const token = peekCoParentInviteToken();
  clearCoParentInviteToken();
  return token;
}

export function clearCoParentInviteToken(): void {
  removeStorage(FAMILY_INVITE_TOKEN_STORAGE_KEY);
}

export function persistPostAuthReturnUrl(url: string): void {
  if (!isSafePostAuthReturnUrl(url)) {
    return;
  }
  writeStorage(FAMILY_RETURN_STORAGE_KEY, url);
  const inviteToken = extractCoParentInviteToken(url);
  if (inviteToken) {
    persistCoParentInviteToken(inviteToken);
  }
}

/**
 * Resolve post-auth family destination from query and/or durable storage.
 * Clears consumed return URL + invite token storage when a destination is found.
 */
export function consumePostAuthReturnUrl(
  queryReturnUrl?: string | null,
): string | null {
  const fromQuery = queryReturnUrl?.trim() ?? '';
  if (fromQuery && isSafePostAuthReturnUrl(fromQuery)) {
    clearPostAuthReturnUrl();
    clearCoParentInviteToken();
    return fromQuery;
  }

  const storedReturn = readStorage(FAMILY_RETURN_STORAGE_KEY);
  if (storedReturn && isSafePostAuthReturnUrl(storedReturn)) {
    clearPostAuthReturnUrl();
    clearCoParentInviteToken();
    return storedReturn;
  }

  const inviteToken = consumeCoParentInviteToken();
  if (inviteToken) {
    clearPostAuthReturnUrl();
    return buildCoParentInviteReturnUrl(inviteToken);
  }

  clearPostAuthReturnUrl();
  return null;
}

export function clearPostAuthReturnUrl(): void {
  removeStorage(FAMILY_RETURN_STORAGE_KEY);
}

function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

function readStorage(key: string): string | null {
  try {
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue) {
      return sessionValue;
    }
  } catch {
    /* private mode */
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function removeStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
